/**
 * Die Originaltexte der Website — also der Stand, mit dem sie ausgeliefert wird.
 *
 * Warum hier und nicht in der Datenbank: Ein Text, der nur in der Datenbank steht,
 * fehlt in jeder frischen Umgebung und ist nicht versioniert. Die Datenbank enthält
 * deshalb ausschließlich die Änderungen des Kunden (Tabelle `site_texts`, V38);
 * ist sie leer, sieht die Website exakt so aus wie hier beschrieben.
 */

export interface TextDef {
  /** Ausgelieferter Text. `**fett**` wird ausgezeichnet. */
  text: string;
  /**
   * Bezeichnung im Bearbeiten-Modus, wenn der Schlüssel allein nicht verrät,
   * worum es geht (z. B. bei Beschriftungen ohne sichtbaren Zusammenhang).
   */
  label?: string;
  /**
   * Abweichende Höchstlänge. Ohne Angabe wird sie aus dem Originaltext abgeleitet
   * (siehe [maxLengthOf]) — das schützt das Layout, ohne 265 Zahlen zu pflegen.
   */
  max?: number;
}

/** Kurzschreibweise erlaubt: Wo nur der Text zählt, steht auch nur der Text. */
export type TextSource = Record<string, string | TextDef>;

export const defOf = (entry: string | TextDef): TextDef =>
  typeof entry === 'string' ? { text: entry } : entry;

/**
 * Zulässige Länge eines Textes.
 *
 * Ohne ausdrückliche Angabe das 1,6-fache des Originals, aufgerundet auf zehn
 * Zeichen und mindestens 60. Der Wert ist keine Wissenschaft, sondern eine Leitplanke:
 * Die Entwürfe sind auf die vorhandenen Textlängen hin gebaut, und eine Überschrift,
 * die statt vier plötzlich zwölf Wörter trägt, sprengt jede Kachel. Wo mehr Luft
 * gebraucht wird, steht `max` am Text.
 */
export const maxLengthOf = (entry: string | TextDef): number => {
  const def = defOf(entry);
  if (def.max) return def.max;
  return Math.max(60, Math.ceil((def.text.length * 1.6) / 10) * 10);
};
